import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VoiceCallsApiClient from '../infrastructure/api/VoiceCallsApiClient';
import { playRemoteMediaElement } from '../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import { VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID } from '../infrastructure/config/voiceWebRtcConfig';
import {
  isVoiceAnswerRequestTimeout,
  isVoiceProviderAnswerFailed
} from '../domain/voiceAnswerErrors';
import { isVoiceAnswerConflictAcceptable } from '../infrastructure/api/VoiceCallsApiClient';
import {
  voiceSetLoading,
  voiceSetError
} from '../../../../actions/voice/voiceActions';

const EMPTY_INCOMING = null;

/**
 * @param {{ enabled?: boolean }} [options]
 */
export default function useVoiceActions(options = {}) {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const activeCall = useSelector(
    (state) => (enabled ? state.voiceReducer.activeCall : EMPTY_INCOMING)
  );

  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;

  const resolveCallControlId = useCallback((callControlId) => {
    if (callControlId) return callControlId;
    return activeCallRef.current?.callControlId ?? null;
  }, []);

  const noop = useCallback(async () => {}, []);

  const answerCall = useCallback(async (callControlId) => {
    if (!enabled) return;
    const resolvedId = resolveCallControlId(callControlId);
    if (!resolvedId) {
      dispatchRef.current(voiceSetError('No callControlId available to answer'));
      return;
    }

    dispatchRef.current(voiceSetLoading(true));
    dispatchRef.current(voiceSetError(null));

    try {
      await VoiceCallsApiClient.answerCall(resolvedId);
      playRemoteMediaElement(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    } catch (error) {
      if (
        isVoiceProviderAnswerFailed(error)
        || isVoiceAnswerRequestTimeout(error)
        || isVoiceAnswerConflictAcceptable(error)
      ) {
        // Caller (answer use case) maps 409 call_ended / already_answered.
        throw error;
      }
      const message = error instanceof Error ? error.message : 'Failed to answer call';
      dispatchRef.current(voiceSetError(message));
      throw error;
    } finally {
      dispatchRef.current(voiceSetLoading(false));
    }
  }, [enabled, resolveCallControlId]);

  const hangupCall = useCallback(async (callControlId, hangupOptions = {}) => {
    if (!enabled) return;
    const resolvedId = resolveCallControlId(callControlId);
    if (!resolvedId) {
      dispatchRef.current(voiceSetError('No callControlId available to hang up'));
      return;
    }

    dispatchRef.current(voiceSetLoading(true));
    dispatchRef.current(voiceSetError(null));

    try {
      await VoiceCallsApiClient.hangupCall(resolvedId, {
        endedReason: hangupOptions.endedReason
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to hang up call';
      dispatchRef.current(voiceSetError(message));
      throw error;
    } finally {
      dispatchRef.current(voiceSetLoading(false));
    }
  }, [enabled, resolveCallControlId]);

  if (!enabled) {
    return { answerCall: noop, hangupCall: noop };
  }

  return {
    answerCall,
    hangupCall
  };
}
